import {
    BS_IMAGE_CAPTION_TYPE,
    BS_IMAGE_FIGURE_TYPE,
} from './constants';
import { findCaptionInFigure, getCaptionText, resolveBsImageFigure, toCaptionPlainText } from './utils';

export function coerceMediaFieldToString(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object' && typeof value.raw === 'string') {
        return value.raw.trim();
    }
    if (typeof value === 'object') {
        for (const entry of Object.values(value)) {
            if (typeof entry === 'string' && entry.trim()) {
                return entry.trim();
            }
        }
    }
    return '';
}

export function extractMediaDescription(media) {
    if (!media) return '';
    return media?.description || '';
}

function getEditorFromComponent(component) {
    return component?.em?.getEditor?.() || component?.em || null;
}

async function disableCaptionEditing(caption) {
    if (!caption) return;

    const view = caption.view;
    if (typeof view?.disableEditing === 'function') {
        await view.disableEditing();
    }

    const editor = getEditorFromComponent(caption);
    if (editor?.getEditing?.()?.cid === caption.cid) {
        editor.RichTextEditor?.disable?.();
    }
}

function writeCaptionHtml(caption, html) {
    const value = html || '';
    caption.empty?.();
    caption.set('content', value, { fromDisable: true });
    const el = caption.getEl?.() || caption.view?.el;
    if (el) {
        el.innerHTML = value;
    }
}

export async function setFigureCaptionFromDescription(figure, description) {
    if (!figure?.get || figure.get('type') !== BS_IMAGE_FIGURE_TYPE) return '';

    const text = coerceMediaFieldToString(description);
    const existing = findCaptionInFigure(figure);

    await disableCaptionEditing(existing);

    if (!text) {
        if (existing) {
            writeCaptionHtml(existing, '');
        }
        return '';
    }

    const caption =
        existing || figure.components().add({ type: BS_IMAGE_CAPTION_TYPE });
    writeCaptionHtml(caption, text);
    caption.removeClass('sio-image-caption--hidden');
    return text;
}

export function getMediaItemSrc(item) {
    if (!item) return '';
    const file = item.file;
    if (file?.previewUrl) {
        return file.previewUrl
            .replace('$width', '1500')
            .replace('$height', '-');
    }
    return file?.downloadUrl || item.url || item.src || '';
}

function revealCaptionAfterMediaReplace(figure, imageTarget, text) {
    const editor = getEditorFromComponent(imageTarget);
    const selected = editor?.getSelected?.();
    const editing = editor?.getEditing?.();
    const selectedFigure =
        resolveBsImageFigure(selected) || resolveBsImageFigure(editing);

    if (
        editor?.select &&
        selected?.cid !== imageTarget.cid &&
        (!selected || selectedFigure?.cid === figure.cid)
    ) {
        editor.select(imageTarget);
    }

    figure.showCaptionEntry?.(text);
}

export async function applyBsImageMediaResult({ imageTarget, figure, result }) {
    if (!result?.url || !imageTarget) return;
    imageTarget.set('src', getMediaItemSrc(result));
    imageTarget.addAttributes({
        src: result.url,
        alt: result.alt ?? '',
        title: result.title ?? '',
    });
    imageTarget.trigger('change:attributes');
    imageTarget.removeClass('gjs-plh-image');

    const targetFigure = figure || resolveBsImageFigure(imageTarget);
    if (!targetFigure) return;

    const text = await setFigureCaptionFromDescription(
        targetFigure,
        extractMediaDescription(result)
    );

    revealCaptionAfterMediaReplace(targetFigure, imageTarget, text);
    setTimeout(() => {
        const caption = findCaptionInFigure(targetFigure);
        const expected = toCaptionPlainText(text);
        if (expected && getCaptionText(caption) !== expected && caption) {
            writeCaptionHtml(caption, text);
        }
        revealCaptionAfterMediaReplace(targetFigure, imageTarget, text);
    }, 40);
}
